import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import { Dialog } from "primereact/dialog";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";

import { useGalleryContext } from "../../Contexts/GalleryContext";
import { toggleDialog } from "../../../redux/Slices/Global/globalSlice";
import { useDeleteMedia } from "../../../queries";
import { deleteItemLocally } from "../../../redux/Slices/scrollSlice";
import { formatSize, formatDuration } from "../../../config/global";
import { formatDateTime } from "../../../utils/datetime";
import QuickShowWizard from "../QuickShow/QuickShowWizard";

import "./previewDialog.css";

/**
 * Media Preview Dialog — 2026 rebuild.
 *
 * Shows the media inside a simulated TV frame so a venue owner can
 * visualize how it'll look on their actual screen. Handles image, video,
 * audio (mp3) and YouTube/web-link types. Renders a details panel and an
 * action bar (Delete / Edit / Show on Screen / Download).
 *
 * Wiring is unchanged: parent toggles redux openDialog.previewMedia and
 * sets selectedItem to this item's id. Pass that item in via `data`.
 *
 * Close: PrimeReact Dialog onHide → dispatches the toggle to flip the
 * flag back off (the prior implementation passed an arrow that called
 * an outer closeDialog that itself dispatched; that path still worked,
 * but the dialog content used inline width+height that fought the
 * maximizable plugin — fixed here by dropping the explicit height).
 */
function PreviewMediaDialog({ data, controlledVisible, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setSelectedItem } = useGalleryContext();
  const openDialog = useSelector((s) => s.global.openDialog);

  const [resolution, setResolution] = useState(null);
  const [quickShowOpen, setQuickShowOpen] = useState(false);

  const deleteMedia = useDeleteMedia();

  /* When `controlledVisible` is supplied, the parent owns visibility
   * via local state (used by CampaignContentSetup to bypass Redux
   * and the dnd-context click swallowing). Otherwise we fall back
   * to the legacy Redux-driven flag so existing callers (Media
   * gallery, etc.) keep working unchanged. */
  const isControlled = controlledVisible !== undefined;
  const visible = isControlled
    ? !!controlledVisible && !!data
    : !!openDialog?.previewMedia && !!data;

  // Read dimensions once the dialog is opened on this item. Re-runs if the
  // selected item changes underneath us.
  useEffect(() => {
    if (!visible) return;
    setResolution(null);
    if (!data?.path || !data?.type) return;
    const t = String(data.type).toUpperCase();
    if (["IMAGE", "GIF", "CORNER"].includes(t)) {
      const img = new Image();
      img.onload = () => setResolution(`${img.naturalWidth} × ${img.naturalHeight}`);
      img.onerror = () => setResolution(null);
      img.src = data.path;
    } else if (t === "VIDEO") {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = data.path;
      v.onloadedmetadata = () => setResolution(`${v.videoWidth} × ${v.videoHeight}`);
    }
  }, [visible, data?.id]);

  const close = () => {
    if (isControlled) {
      onClose?.();
      return;
    }
    if (openDialog?.previewMedia) dispatch(toggleDialog("previewMedia"));
    setSelectedItem("");
  };

  const onEdit = () => {
    dispatch(toggleDialog("updateMedia"));
  };

  const onDelete = () => {
    confirmDialog({
      message: t('showmedia.confirm_delete_msg', { name: (data.file_name || "").replace(/_/g, " ") }),
      header: t('buttons.confirm'),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteMedia.mutate({ id: data.id }, {
          onSuccess: () => {
            dispatch(deleteItemLocally(data.id));
            close();
          },
        });
      },
    });
  };

  const onDownload = () => {
    if (!data?.path) return;
    const a = document.createElement("a");
    a.href = data.path;
    a.download = data.file_name || "";
    a.target = "_blank";
    a.rel = "noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (!data) return null;

  const ext = (data.file_name || "").split(".").pop().toUpperCase();
  const format =
    data.section === "LINKS"
      ? data.type === "YOUTUBE"
        ? "YouTube"
        : (data.type || "Link")
      : `${data.type || ""}${ext && ext !== (data.type || "").toUpperCase() ? " / " + ext.toLowerCase() : ""}`;

  const showDuration = ["VIDEO", "AUDIO", "YOUTUBE"].includes(String(data.type || "").toUpperCase());

  return (
    <>
      <ConfirmDialog />
      <Dialog
        visible={visible}
        onHide={close}
        modal
        maximizable
        dismissableMask
        closeOnEscape
        className="pp-preview-dialog"
        breakpoints={{ "960px": "96vw", "640px": "100vw" }}
        style={{ width: "min(96vw, 1100px)" }}
        header={
          <div className="pp-preview-header">
            <i className={`pi ${iconForType(data.type)} pp-preview-header-icon`} />
            <span className="pp-preview-header-title" title={data.file_name}>
              {(data.file_name || "").replace(/_/g, " ")}
            </span>
          </div>
        }
      >
        <div className="pp-preview-body">
          <div className="pp-preview-stage">
            <div className="pp-tv-frame">
              <div className="pp-tv-screen">
                <PreviewContent data={data} />
              </div>
            </div>
            <div className="pp-tv-stand-neck" />
            <div className="pp-tv-stand-base" />
          </div>

          <aside className="pp-preview-details">
            <h3 className="pp-preview-details-title">{t('showmedia.details_title')}</h3>
            <dl className="pp-preview-details-list">
              <Detail label={t('showmedia.format')} value={format || "—"} />
              <Detail label={t('showmedia.file_size')} value={data.size ? formatSize(data.size) : "—"} />
              <Detail
                label={t('dragdrop.duration')}
                value={showDuration && data.duration ? formatDuration(data.duration) : "—"}
              />
              <Detail label={t('showmedia.uploaded_date')} value={formatDateTime(data.created_at)} />
              {resolution && <Detail label={t('showmedia.resolution')} value={resolution} />}
              {data?.user?.name && <Detail label={t('showmedia.uploaded_by')} value={data.user.name} />}
            </dl>
          </aside>
        </div>

        <div className="pp-preview-actions">
          <button
            type="button"
            className="pp-preview-action pp-preview-action-danger"
            onClick={onDelete}
            disabled={deleteMedia.isLoading}
          >
            <i className="pi pi-trash" /> {t('buttons.delete')}
          </button>
          <button type="button" className="pp-preview-action" onClick={onEdit}>
            <i className="pi pi-pencil" /> {t('buttons.edit')}
          </button>
          <button
            type="button"
            className="pp-preview-action pp-preview-action-primary"
            onClick={() => setQuickShowOpen(true)}
          >
            <i className="pi pi-desktop" /> {t('showmedia.show_on_screen')}
          </button>
          <button type="button" className="pp-preview-action" onClick={onDownload}>
            <i className="pi pi-download" /> {t('buttons.download')}
          </button>
        </div>
      </Dialog>

      <QuickShowWizard
        open={quickShowOpen}
        onClose={() => setQuickShowOpen(false)}
        media={
          quickShowOpen
            ? {
                id: data.id,
                title: (data.file_name || "").replace(/_/g, " "),
                file_name: data.file_name,
                thumb: data.thumb,
                type: data.type,
                duration: data.duration,
              }
            : null
        }
      />
    </>
  );
}

function Detail({ label, value }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function iconForType(type) {
  switch (String(type || "").toUpperCase()) {
    case "IMAGE":
    case "GIF":
    case "CORNER":
      return "pi-image";
    case "VIDEO":
      return "pi-video";
    case "AUDIO":
      return "pi-volume-up";
    case "YOUTUBE":
      return "pi-youtube";
    case "EXTERNAL_LINK":
    case "WEBVIEW":
    case "DRIVE":
      return "pi-link";
    default:
      return "pi-file";
  }
}

function PreviewContent({ data }) {
  const { t: t2 } = useTranslation();
  const t = String(data?.type || "").toUpperCase();

  if (t === "IMAGE" || t === "CORNER" || t === "GIF") {
    return (
      <img
        src={data.path}
        alt={data.file_name || ""}
        className="pp-tv-content pp-tv-img"
      />
    );
  }

  if (t === "VIDEO") {
    if ((data.path || "").endsWith(".avi")) {
      return (
        <div className="pp-tv-fallback">
          <i className="pi pi-exclamation-circle" />
          <p>{t2('showmedia.avi_not_supported')}</p>
          <a href={data.path} download>{t2('buttons.download')}</a>
        </div>
      );
    }
    return (
      <video className="pp-tv-content pp-tv-video" controls preload="metadata">
        <source src={data.path} />
      </video>
    );
  }

  if (t === "AUDIO") {
    return (
      <AudioPlayer
        src={data.path}
        title={(data.file_name || "").replace(/_/g, " ")}
      />
    );
  }

  if (t === "YOUTUBE") {
    const id = extractYoutubeId(data.path || "");
    const src = id ? `https://www.youtube.com/embed/${id}` : data.path;
    return (
      <iframe
        className="pp-tv-content pp-tv-iframe"
        src={src}
        title={data.file_name || "YouTube preview"}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (t === "EXTERNAL_LINK" || t === "WEBVIEW" || t === "DRIVE") {
    return (
      <iframe
        className="pp-tv-content pp-tv-iframe"
        src={data.path}
        title={data.file_name || "Web preview"}
      />
    );
  }

  return (
    <div className="pp-tv-fallback">
      <i className="pi pi-file" />
      <p>{data.file_name || t2('showmedia.unsupported_media')}</p>
      {data.path && (
        <a href={data.path} target="_blank" rel="noreferrer">{t2('showmedia.open_file')}</a>
      )}
    </div>
  );
}

function AudioPlayer({ src, title }) {
  return (
    <div className="pp-tv-audio">
      <i className="pi pi-volume-up pp-tv-audio-icon" />
      <div className="pp-tv-audio-title" title={title}>{title}</div>
      <audio
        controls
        src={src}
        preload="metadata"
        className="pp-tv-audio-controls"
      />
    </div>
  );
}

function extractYoutubeId(url = "") {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split("/")[0];
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const m = u.pathname.match(/(?:shorts|embed)\/([^/]+)/);
    if (m) return m[1];
  } catch {
    /* not a URL */
  }
  return null;
}

export default PreviewMediaDialog;
